import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import DataProvider from '../../provider';
import Button from '@material-ui/core/Button';
import Funnel from '../../templates/contents/Funnel';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { FeedbackContainer } from '../../components/feedback';
import { OrderItemCard } from './components/OrderItemCard';
import { BottomDrawer } from './components/OrderBottomDrawer';

const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
        padding: theme.spacing(2, 0),
    },
    orderHeader: {
        margin: theme.spacing(0, 0, 1),
    },
}));

export interface B2CLockerSellingOrderCart {
    sme?: boolean;
    totalWithTax: number;
    items: Array<B2CLockerSellingOrderCartItem>;
    customSuccessMessage?: string | null;
}

export interface B2CLockerSellingOrderCartItem {
    id: number;
    itemSaleableId: number;
    label: string;
    quantity: number;
    totalWithTax: number;
    unitPriceWithTax: number;
    photoUrl?: string;
}

export enum OrderDataStatus {
	FETCH_DATA = "fetch_data",
	FETCHING_DATA = "fetching_data",
	DATA_FETCHED = "data_fetched",
	ERROR_DATA_FETCHING = "error_data_fetching",
	FETCHING_DATA_MAX_ATTEMPT = "fetching_data_max_attempt",
    REQUEST_REOPEN = "request_reopen",
}

export enum OrderActionType {
	failed = "failed",
	completed = "completed",
	openingLocker = "openingLocker",
	openingLockerError = "openingLockerError",
	awaitingPayment = "awaitingPayment"
}

export default (props: any) => {

	let history = useHistory();
	let params = useParams();
	let id = params['id'];
	const [status, setStatus] = useState<OrderDataStatus>(OrderDataStatus.FETCH_DATA);
	const [orderStatus, setOrderStatus] = useState<OrderActionType>(OrderActionType.awaitingPayment);
    const [localCart, setLocalCart] = useState<B2CLockerSellingOrderCart | null>(null);
	const [retryCount, setRetryCount] = useState<number>(0);
    const [showReopenButton, setShowReopenButton] = useState<boolean>(false);
    const [orderBehavior, setOrderBehavior] = useState({})
    const classes = useStyles();

	const dataProvider = DataProvider(process.env.REACT_APP_WAREHOUSE_API || '');

	useEffect(() => {
		let timeout: null | NodeJS.Timeout = null;
        let reopenTimeout: null | NodeJS.Timeout = null;

		if(id && OrderDataStatus.FETCH_DATA === status) {
			if(retryCount < 7) {
				setStatus(OrderDataStatus.FETCHING_DATA);

				dataProvider(`b2c/locker/selling-order/info/${id}`)
					.then(info => {
						setStatus(OrderDataStatus.DATA_FETCHED);
						if(info.actionType === OrderActionType.completed) {
							if (reopenTimeout) {
								clearTimeout(reopenTimeout);
							}
							setShowReopenButton(true);
							reopenTimeout = setTimeout(() => {
								setShowReopenButton(false);
							}, 60000);
						}
                        setOrderStatus(info.actionType);

                        setLocalCart(info.lockerSellingOrder)
                        if(info.loading) {
                            if(timeout) {
                                clearTimeout(timeout);
                            }
                            timeout = setTimeout(() => {
                                setRetryCount(retryCount + 1);
                                setStatus(OrderDataStatus.FETCH_DATA);
                            }, 2000);
                        }

                        if(info.orderBehavior) {
                            setOrderBehavior(info.orderBehavior);
                        }
					})
					.catch(e => {
						setStatus(OrderDataStatus.ERROR_DATA_FETCHING);
					})
			}
			else {
				setStatus(OrderDataStatus.FETCHING_DATA_MAX_ATTEMPT);
			}
		}

		return () => {
			if(timeout) {
				clearTimeout(timeout);
			}
			if(reopenTimeout) {
				clearTimeout(reopenTimeout);
			}
		}
    }, [id, status, retryCount, dataProvider, history])

    let content;

    if(status === OrderDataStatus.DATA_FETCHED) {
        switch (orderStatus) {
            case OrderActionType.openingLockerError:
            case OrderActionType.completed:
                content = (<Content>
                    {/* HEADER */}
                    {localCart?.customSuccessMessage && <>
                        <Text><div dangerouslySetInnerHTML={{__html:localCart.customSuccessMessage.replaceAll("amp;", "")}}/></Text>
                    </>}
                    {!localCart?.customSuccessMessage &&<>
                        <Typography variant="body2" align="center">Apri il fridge e ritira il tuo ordine!</Typography>
                        <Typography variant="caption" align="center">la maniglia è a sinistra e trovi le posate in basso <span role="img" aria-label="Fork and Knife Emoji">🍴</span></Typography>
                    </>}
                    {/* PRODUCTS */}
                    <Container className={classes.cardContainer} component="main" maxWidth="xs">
                        <Typography variant='caption' className={classes.orderHeader} color='primary' style={{display: 'block'}} align='center'>
                            Riepilogo ordine
                        </Typography>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                            {localCart?.items.map((row, key) => {
                                return (
                                    <OrderItemCard key={`order-detail-row-${key}`} item={row}></OrderItemCard>
                                )
                            })}
                        </div>
                    </Container>
                </Content>);
                break;
            case OrderActionType.failed:
                content = <Text>Ordine Fallito</Text>
                break;
            case OrderActionType.openingLocker:
                content = <Content><Text>Apertura Frigo in corso</Text> <CircularProgress color="primary"></CircularProgress></Content>
                break;
            case OrderActionType.awaitingPayment:
                content = <Content><Text> Pagamento in corso </Text></Content>
                break;
        }
    }

	if(status === OrderDataStatus.FETCHING_DATA_MAX_ATTEMPT) {
        content = (<Content>
                <Text>Ordine in corso</Text>
                <Button
                    onClick={() => {
                        setRetryCount(0);
                        setStatus(OrderDataStatus.FETCH_DATA);
                    }}
                    fullWidth={true}
                    variant="contained"
                    size="large"
                    color="primary"
                >
                    RICARICA
                </Button>
            </Content>
		);
    }

    return (
		<Funnel title={"Ordine"} loading={[OrderDataStatus.FETCHING_DATA, OrderDataStatus.FETCH_DATA, OrderDataStatus.REQUEST_REOPEN].includes(status)} back={() => history.push(`/`)}>
            <div style={{display: 'flex', flexDirection: 'column', width: '100%', height: 'calc(100dvh - 56px)', justifyContent: 'space-between'}}>
                <div style={{flex: 1, overflowX: 'auto'}}>
                    <Container component="main" maxWidth="xs">
                        {content}
                    </Container>
                    {
                        status === OrderDataStatus.DATA_FETCHED &&
                        [
                            OrderActionType.openingLockerError,
                            OrderActionType.completed,
                        ].includes(orderStatus) &&
                            <FeedbackContainer/>
                    }
                </div>
                <BottomDrawer
                    status={status}
                    orderStatus={orderStatus}
                    showReopenButton={showReopenButton}
                    setStatus={setStatus}
                    dataProvider={dataProvider}
                    id={id}
                    setRetryCount={setRetryCount}
                    orderBehavior={orderBehavior}
                />
            </div>
		</Funnel>
	);
}

function Content(props: any) {
    return <div className={props.className} style={{width: '100%', minHeight: '200px', padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
        {props.children}
    </div>
}

function Text(props: any) {
    return (<Typography style={{padding: '16px', width: '100%', textAlign: 'center'}}>
        {props.children}
    </Typography>)
}
